import { HttpErrorResponse, HttpResponse, HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  catchError,
  EMPTY,
  exhaustMap,
  finalize,
  map,
  mergeMap,
  of,
  switchMap,
  take,
  tap,
} from 'rxjs';

import { TranslateService } from '@ngx-translate/core';

import { FRONT_LOADING_ROUTES } from 'src/app/front-loading/routes';
import { ROUTES } from 'src/app/remote-pickup/routes';
import { CustomSnackbarService } from 'src/app/shared/components/custom-snackbar/custom-snackbar.service';
import { FrodoService, PICKUP_STATUS, PickUpStatus } from 'src/app/shared/services/frodo';
import { NavigationService } from 'src/app/shared/services/navigation/navigation.service';
import { APP_ROUTES } from 'src/app/shared/utils';
import { DISPENSE_STATES, TOAST_TYPES } from 'src/app/shared/utils/constants';
import * as fromGlobal from 'src/app/stores/global';
import { TTW_ROUTES } from 'src/app/ttw/routes';

import { ERROR_CODES } from './constants';
import {
  AddPickupErrorResponse,
  AddPickupResponse,
  FinishPickupRequest,
  StartPickupResponse,
} from './interfaces';
import { ERROR_MESSAGES } from './messages';
import * as RemotePickupActions from './remote-pickup.actions';
import * as RemotePickupSelectors from './remote-pickup.selector';

@Injectable()
export class RemotePickupEffects {
  private dispenseStateErrorMsg: Record<string, string> = {
    [DISPENSE_STATES.RECLAIMED]: ERROR_MESSAGES.ORDER_ALREADY_RECLAIMED,
    [DISPENSE_STATES.REMOVED]: ERROR_MESSAGES.ORDER_ALREADY_RECLAIMED,
    [DISPENSE_STATES.DISPENSED]: ERROR_MESSAGES.ORDER_ALREADY_PICKED_UP,
    [DISPENSE_STATES.LOADED_VERIFY]: ERROR_MESSAGES.SEE_ASSOCIATE,
    [DISPENSE_STATES.LOCKEDDOWN]: ERROR_MESSAGES.SEE_ATTENDANT,
    [DISPENSE_STATES.READY]: ERROR_MESSAGES.ORDER_NOT_READY_FOR_PICKUP,
    [DISPENSE_STATES.LOADING]: ERROR_MESSAGES.ORDER_NOT_READY_FOR_PICKUP,
    [DISPENSE_STATES.ORDERED]: ERROR_MESSAGES.ORDER_NOT_READY_FOR_PICKUP,
  };

  private hasShownUpdateStateError = false;

  constructor(
    private readonly actions$: Actions,
    private readonly customSnackbar: CustomSnackbarService,
    private readonly frodoService: FrodoService,
    private readonly router: Router,
    private readonly navigationService: NavigationService,
    private readonly translateService: TranslateService,
    private readonly store: Store
  ) {}

  addPickup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemotePickupActions.addPickup),
      exhaustMap(({ code, isDriverNotificationEnabled }) => {
        this.store.dispatch(fromGlobal.setIsLoading({ isLoading: true }));
        return this.frodoService.addPickup(code).pipe(
          map((res: HttpResponse<AddPickupResponse>) => {
            const status = res.status;
            const response = res.body;

            if (response.isOverflowArea) {
              this.store.dispatch(fromGlobal.setIsLoading({ isLoading: false }));
              return this.handleOverflowPickup(response);
            }

            const result = this.handleSuccessfulPickup(
              code,
              response,
              status == HttpStatusCode.Accepted
            );
            this.store.dispatch(fromGlobal.setIsLoading({ isLoading: false }));
            return result;
          }),
          catchError((error) => {
            this.store.dispatch(fromGlobal.setIsLoading({ isLoading: false }));
            if (this.isCodeNotFoundError(error) && isDriverNotificationEnabled) {
              this.store.dispatch(
                RemotePickupActions.setShouldHandleDriverNotification({
                  shouldHandleDriverNotification: true,
                })
              );
              return of(RemotePickupActions.addPickupFailure());
            }
            const message = this.mapErrorToMessage(code, error);
            this.customSnackbar.openSnackBar(message, TOAST_TYPES.Error);
            return of(RemotePickupActions.addPickupFailure());
          })
        );
      })
    )
  );

  updatePickupState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemotePickupActions.updatePickupState),
      switchMap(() =>
        this.store.select(RemotePickupSelectors.selectRemotePickupScheduleId).pipe(
          take(1),
          mergeMap((scheduleId) => {
            if (!scheduleId) {
              this.router.navigate([ROUTES.REMOTE_PICKUP]);
              return of(fromGlobal.setIsLoading({ isLoading: false }));
            }
            return this.frodoService.getPickupState(scheduleId).pipe(
              map((response) => {
                this.hasShownUpdateStateError = false;
                return RemotePickupActions.updatePickupStateSuccess({
                  status: response.state as PickUpStatus,
                  position: response.position,
                });
              }),
              catchError(() => {
                if (!this.hasShownUpdateStateError) {
                  this.hasShownUpdateStateError = true;
                  this.customSnackbar.openSnackBar(
                    ERROR_MESSAGES.PICKUP_STATE_UPDATE_FAILED,
                    TOAST_TYPES.Error
                  );
                }
                return of(fromGlobal.setIsLoading({ isLoading: false }));
              })
            );
          })
        )
      )
    )
  );

  redirectBasedOnState$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RemotePickupActions.redirectBasedOnState),
        switchMap(() =>
          this.store.select(RemotePickupSelectors.selectRemotePickup).pipe(
            take(1),
            tap((remotePickupState) => {
              const isDispenseStatePending =
                remotePickupState?.state?.status === PICKUP_STATUS.PENDING;
              if (isDispenseStatePending) {
                if (!this.isTTWView()) {
                  this.router.navigate([ROUTES.CONFIRM_PICKUP]);
                  this.store.dispatch(fromGlobal.setIsLoading({ isLoading: false }));
                }
              }
            })
          )
        )
      ),
    { dispatch: false }
  );

  updateWaitingQueueState$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RemotePickupActions.updateWaitingQueueState),
        switchMap(() =>
          this.store.select(RemotePickupSelectors.selectRemotePickupScheduleId).pipe(
            take(1),
            switchMap((scheduleId) => {
              if (scheduleId) {
                this.store.dispatch(RemotePickupActions.updatePickupState());
                return this.actions$.pipe(
                  ofType(RemotePickupActions.updatePickupStateSuccess),
                  take(1),
                  tap(() => {
                    this.store.dispatch(RemotePickupActions.redirectBasedOnState());
                    this.store.dispatch(fromGlobal.setIsLoading({ isLoading: false }));
                  }),
                  catchError(() => {
                    this.store.dispatch(fromGlobal.setIsLoading({ isLoading: false }));
                    return EMPTY;
                  })
                );
              } else {
                this.navigateHome();
                this.store.dispatch(fromGlobal.setIsLoading({ isLoading: false }));
                return EMPTY;
              }
            })
          )
        )
      ),
    { dispatch: false }
  );

  startPickup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemotePickupActions.startPickup),
      mergeMap(({ scheduleId, isOverflowArea }) => {
        this.store.dispatch(fromGlobal.setIsLoading({ isLoading: true }));
        return this.frodoService.startPickup(scheduleId).pipe(
          map((response: StartPickupResponse) => {
            if (!isOverflowArea) {
              const { orderPickup } = this.getRoutesPerContext();
              this.router.navigate([orderPickup]);
            }
            return RemotePickupActions.startPickupSuccess(response);
          }),
          catchError(() => {
            this.customSnackbar.openSnackBar(ERROR_MESSAGES.PICKUP_START_FAILED, TOAST_TYPES.Error);
            this.navigateHome();
            return of(RemotePickupActions.startPickupFailure());
          }),
          finalize(() => {
            this.store.dispatch(fromGlobal.setIsLoading({ isLoading: false }));
          })
        );
      })
    )
  );

  finishPickup$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RemotePickupActions.finishPickup),
      mergeMap(({ scheduleId, code, isOffline }) => {
        const request: FinishPickupRequest = { scheduleId };
        if (isOffline) {
          request.isOffline = isOffline;
          request.code = code;
        }
        return this.frodoService.finishPickup(request).pipe(
          mergeMap(() => [
            RemotePickupActions.finishPickupSuccess(),
            RemotePickupActions.clearRemotePickup(),
          ]),
          catchError(() =>
            of(RemotePickupActions.finishPickupFailure(), RemotePickupActions.clearRemotePickup())
          )
        );
      })
    )
  );

  /** Only reachable from the waiting queue screens, which always live under the TTW routes. */
  private isTTWView(): boolean {
    return this.router.url.includes(APP_ROUTES.TTW);
  }

  /** True while the pickup runs on the device (TTW or array front-loading), false on mobile. */
  private isDevicePickupView(): boolean {
    return (
      this.router.url.includes(APP_ROUTES.TTW) ||
      this.router.url.includes(FRONT_LOADING_ROUTES.ENTER_SCAN_CODES)
    );
  }

  private getRoutesPerContext(): Record<'waitingQueue' | 'orderPickup', string> {
    if (this.isDevicePickupView()) {
      return {
        waitingQueue: TTW_ROUTES.WAITING_QUEUE,
        orderPickup: TTW_ROUTES.DISPENSING_ORDER,
      };
    }
    return {
      waitingQueue: ROUTES.WAITING_QUEUE,
      orderPickup: ROUTES.ORDER_PICKUP,
    };
  }

  /** Back to the screen where the code is entered. */
  private navigateHome(): void {
    if (this.isDevicePickupView()) {
      this.navigationService.navigateToPickupEntry();
      return;
    }
    this.router.navigate([ROUTES.REMOTE_PICKUP]);
  }

  private handleSuccessfulPickup(
    code: string,
    response: AddPickupResponse,
    isQueued = false
  ):
    | ReturnType<typeof RemotePickupActions.addPickupSuccess>
    | ReturnType<typeof RemotePickupActions.startPickup> {
    if (isQueued) {
      const { waitingQueue } = this.getRoutesPerContext();
      this.router.navigate([waitingQueue]);

      return RemotePickupActions.addPickupSuccess({
        code,
        schedule: response.schedule,
        spaces: response.spaces,
        queuedAt: response.queuedAt ?? 0,
        position: response.position ?? 0,
        status: response.status as PickUpStatus,
      });
    } else if (response.isOffline) {
      return RemotePickupActions.addPickupSuccess({
        code,
        schedule: response.schedule,
        spaces: response.spaces,
        isOffline: true,
      });
    } else {
      return RemotePickupActions.startPickup({ scheduleId: response.schedule.id });
    }
  }

  private handleOverflowPickup(
    response: AddPickupResponse
  ): ReturnType<typeof RemotePickupActions.addPickupSuccess> {
    return RemotePickupActions.addPickupSuccess({
      ...response,
      isOverflowArea: true,
      spaces: response.spaces ?? [],
    });
  }

  private mapErrorToMessage(codeNumber: string, error: unknown): string {
    const httpError = error as HttpErrorResponse;
    const errorResponse = httpError.error as AddPickupErrorResponse | undefined;

    switch (errorResponse?.errorCode) {
      case ERROR_CODES.INVALID_CODE:
        return ERROR_MESSAGES.INVALID_CODE;
      case ERROR_CODES.CODE_NOT_FOUND:
        return ERROR_MESSAGES.CODE_NOT_FOUND;
      case ERROR_CODES.INVALID_DISPENSE_STATE: {
        const currentState = errorResponse.details?.currentState;
        return this.dispenseStateErrorMsg[currentState] || ERROR_MESSAGES.CODE_NOT_FOUND;
      }
      case ERROR_CODES.PICKUP_IN_ANOTHER_DEVICE: {
        const deviceName = errorResponse.details?.assignedDeviceName;
        return this.translateService.instant(ERROR_MESSAGES.PICKUP_IN_ANOTHER_DEVICE, {
          deviceName,
          codeNumber,
        });
      }
      default:
        return ERROR_MESSAGES.PICKUP_UNAVAILABLE;
    }
  }

  private isCodeNotFoundError(error: unknown): boolean {
    const httpError = error as HttpErrorResponse;
    const errorResponse = httpError.error as AddPickupErrorResponse | undefined;
    return errorResponse?.errorCode === ERROR_CODES.CODE_NOT_FOUND;
  }
}
